/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/jsx-fragments */
import * as React from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Button from "@mui/material/Button";
import { v4 as uuidv4 } from "uuid";
import { useRegistrarHojaDeVidaMutation } from "src/redux/api/aspirantesApi";
import {
  Alert,
  AlertTitle,
  useTheme,
  useMediaQuery,
  Typography,
} from "@mui/material";
import InformacionBasicaForm from "./informacion-basica/InformacionBasica";
import DocumentosRequeridosForm from "./documentos/DocumentosRequeridosForm";
import { valoresInicialesInformacionBasica } from "./informacion-basica/utils";
import ExperienciaLaboralForm from "./experiencia-laboral/ExperienciaLaboral";
import { valoresInicialesEsperienciaLaboral } from "./experiencia-laboral/utils";
import { buildData } from "./utils";
import { valoresInicialesDocumentos } from "./documentos/utils";
import { PerfilesId } from "@/utils/perfil";
import Recomendaciones from "./Recomendaciones";

// Define types to use in the component
interface StepItem {
  label: string;
  node: React.ReactNode;
}

interface StepLabelProps {
  optional?: React.ReactNode;
}

interface StepProps {
  completed?: boolean;
}

// Component for displaying form steps
const FormSteps: React.FC<{
  steps: StepItem[];
  activeStep: number;
  isStepSkipped: (step: number) => boolean;
  isMobile: boolean;
}> = ({ steps, activeStep, isStepSkipped, isMobile }) => {
  return (
    <Stepper
      activeStep={activeStep}
      alternativeLabel={isMobile}
      sx={{
        "& .MuiStepLabel-label": {
          fontSize: isMobile ? "14px" : "inherit",
          mt: isMobile ? 1 : 0,
        },
      }}
    >
      {steps.map((step, index) => {
        const stepProps: StepProps = {};
        const labelProps: StepLabelProps = {};

        if (isStepSkipped(index)) {
          stepProps.completed = false;
        }

        return (
          <Step key={step.label} {...stepProps}>
            <StepLabel {...labelProps}>{step.label}</StepLabel>
          </Step>
        );
      })}
    </Stepper>
  );
};

// Component for displaying success message
const SuccessMessage: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  return (
    <Box
      sx={{
        p: isMobile ? 3 : 10,
        textAlign: "justify",
        my: isMobile ? 3 : 5,
      }}
    >
      <Alert
        severity="success"
        sx={{
          fontSize: isMobile ? 14 : 16,
          alignItems: "flex-start",
        }}
      >
        <AlertTitle
          sx={{
            fontWeight: "medium",
            fontSize: isMobile ? 16 : 18,
          }}
        >
          Respuesta registrada
        </AlertTitle>
        <Typography sx={{ fontSize: isMobile ? 14 : 16 }}>
          Hemos recibido su información, su hoja de vida será valorada por el
          equipo técnico de IUTraining para determinar experiencia específica y
          disponibilidad presencial con la intensidad horaria requerida y
          posterior validación del Ministerio de las TIC.
        </Typography>
      </Alert>
    </Box>
  );
};

// Component for displaying error message
const ErrorMessage: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  return (
    <Box sx={{ my: isMobile ? 3 : 5 }}>
      <Alert
        severity="error"
        sx={{
          fontSize: isMobile ? 14 : 16,
          alignItems: "flex-start",
        }}
      >
        <AlertTitle sx={{ fontSize: isMobile ? 16 : 18 }}>Error</AlertTitle>
        <Typography sx={{ fontSize: isMobile ? 14 : 16 }}>
          Ocurrió un error al registrar la información, por favor intenta de
          nuevo.
        </Typography>
      </Alert>
    </Box>
  );
};

// Component for reset button
const ResetButton: React.FC<{
  handleReset: () => void;
  isMobile: boolean;
}> = ({ handleReset, isMobile }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        pt: isMobile ? 3 : 2,
        justifyContent: "center",
      }}
    >
      <Button
        onClick={handleReset}
        variant="contained"
        size={isMobile ? "large" : "medium"}
        sx={{
          py: isMobile ? 1.5 : 1,
          px: isMobile ? 3 : 2,
        }}
      >
        Enviar otro registro
      </Button>
    </Box>
  );
};

// Component for completion view (success/error)
const CompletionView: React.FC<{
  respuestaRegistrada: boolean;
  error: boolean;
  handleReset: () => void;
  isMobile: boolean;
}> = ({ respuestaRegistrada, error, handleReset, isMobile }) => {
  return (
    <React.Fragment>
      {respuestaRegistrada && <SuccessMessage isMobile={isMobile} />}
      {error && <ErrorMessage isMobile={isMobile} />}
      <ResetButton handleReset={handleReset} isMobile={isMobile} />
    </React.Fragment>
  );
};

// Main component
export default function HorizontalLinearStepper() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // State definitions
  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set<number>());
  const [aspiranteId, setAspiranteId] = React.useState(uuidv4());
  const [informacionBasica, setInformacionBasica] = React.useState(
    valoresInicialesInformacionBasica,
  );
  const [experienciaLaboral, setExperienciaLaboral] = React.useState(
    valoresInicialesEsperienciaLaboral,
  );
  const [documentos, setDocumentos] = React.useState(
    valoresInicialesDocumentos,
  );

  // API mutation hook
  const [
    registrarHojaDeVida,
    { isSuccess: respuestaRegistrada, isError: error },
  ] = useRegistrarHojaDeVidaMutation();

  // Helper functions
  const isStepSkipped = (step: number) => {
    return skipped.has(step);
  };

  const handleNext = () => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSend = (documentosForm: any) => {
    const request = buildData(
      informacionBasica,
      experienciaLaboral,
      documentosForm,
      aspiranteId,
    );
    registrarHojaDeVida({ informacion: request });
  };

  const handleReset = () => {
    setInformacionBasica(valoresInicialesInformacionBasica);
    setExperienciaLaboral(valoresInicialesEsperienciaLaboral);
    setDocumentos(valoresInicialesDocumentos);
    setActiveStep(0);
  };

  // Define steps with their labels and components
  const createSteps = (): StepItem[] => {
    return [
      {
        label: "Recomendaciones",
        node: (
          <Recomendaciones  handleNext={handleNext}/>
        ),
      },
      {
        label: "Información básica",
        node: (
          <InformacionBasicaForm
            handleNext={handleNext}
            setInformacionBasica={setInformacionBasica}
            informacionBasica={informacionBasica}
            aspiranteId={aspiranteId}
          />
        ),
      },
      {
        label: "Experiencia profesional",
        node: (
          <ExperienciaLaboralForm
            onSubmit={() => {}}
            perfilSeleccionado={informacionBasica.perfilId as PerfilesId}
            handleBack={handleBack}
            handleNext={handleNext}
            setExperienciaLaboral={setExperienciaLaboral}
            experienciaLaboral={experienciaLaboral}
            aspiranteId={aspiranteId}
          />
        ),
      },
      {
        label: "Documentos requeridos",
        node: (
          <DocumentosRequeridosForm
            onSubmit={() => {}}
            handleBack={handleBack}
            handleNext={handleNext}
            handleSend={handleSend}
            setDocumentos={setDocumentos}
            documentos={documentos}
            aspiranteId={aspiranteId}
          />
        ),
      },
    ];
  };

  const steps = createSteps();

  return (
    <Box
      sx={{
        width: "100%",
        py: isMobile ? 2 : 3,
        px: isMobile ? 1 : 0,
      }}
    >
      {/* Stepper component */}
      <FormSteps
        steps={steps}
        activeStep={activeStep}
        isStepSkipped={isStepSkipped}
        isMobile={isMobile}
      />

      {/* Content section */}
      {activeStep === steps.length ? (
        <CompletionView
          respuestaRegistrada={respuestaRegistrada}
          error={error}
          handleReset={handleReset}
          isMobile={isMobile}
        />
      ) : (
        <Box py={isMobile ? 1 : 2}>{steps[activeStep].node}</Box>
      )}
    </Box>
  );
}
